import React from "react";
import VenmoIcon from "../components/icons/VenmoIcon";
import { ItemUI } from "../types/item";
import { User } from "../types/user";
import { useSplitCalc } from "../hooks/useSplitCalc";
import { constructVenmoUrl } from "../utils/venmo";
import "./UserSplitTotal.scss";

interface UserSplitTotalProps {
  items: ItemUI[];
  tip: number;
  tax: number;
  selectedUser: User | null;
  paidUserVenmo?: string;
  // the payer (the first mate) — anyone else selected pays them back
  paidUserId?: string;
  // the viewer created this receipt (Request) rather than a guest (Pay)
  isOwner: boolean;
  // compact: just the name, total, and venmo button (the fixed bottom bar)
  compact?: boolean;
  // false hides the Pay/Request button (the full breakdown, when the compact
  // bar already carries it)
  showVenmo?: boolean;
}

const UserSplitTotal: React.FC<UserSplitTotalProps> = ({
  items,
  tip,
  tax,
  selectedUser,
  paidUserVenmo,
  paidUserId,
  isOwner,
  compact = false,
  showVenmo = true,
}) => {
  const {
    subtotal: userSubtotal,
    tax: userTax,
    tip: userTip,
  } = useSplitCalc(items, tax, tip, selectedUser);

  if (selectedUser === null) {
    // keep the bottom bar from looking half-empty
    return compact ? (
      <div className="user-split-compact empty">Select who you are</div>
    ) : null;
  }

  const userTotal = userSubtotal + userTax + userTip;
  const taxPct = userSubtotal > 0 ? (userTax / userSubtotal) * 100 : 0;
  const tipPct = userSubtotal > 0 ? (userTip / userSubtotal) * 100 : 0;
  // Venmo note: the app name plus a link back to this check, opened as the
  // mate being paid for / charged (?user=), so tapping it in Venmo lands them
  // on their own split
  const checkUrl = `${window.location.origin}${window.location.pathname}?user=${encodeURIComponent(selectedUser.id)}`;
  const note = `Split produced by CheckMates: ${checkUrl}`;

  // The Venmo slot, by who's selected and who's looking:
  //  - the payer (first mate) selected: no button — they fronted the bill
  //  - anyone else, viewed by the receipt's creator: Request their total
  //  - anyone else, viewed by a guest: Pay their total to the payer
  // Both need the payer's venmo; until it's set, a hint says so instead.
  const isPayer = selectedUser.id === paidUserId;
  let venmoAction: React.ReactNode = null;
  if (showVenmo) {
    if (isPayer) {
      venmoAction = <span className="venmo-note">Fronted the bill</span>;
    } else if (!paidUserVenmo) {
      venmoAction = (
        <span className="venmo-note">
          {isOwner ? "Set your Venmo to Request" : "No Venmo to pay yet"}
        </span>
      );
    } else {
      const pay = !isOwner;
      // Pay sends to the payer; Request asks the selected mate (prefilled
      // when they have a venmo, otherwise picked in Venmo)
      const recipient = pay ? paidUserVenmo : selectedUser.venmo;
      venmoAction = (
        <a
          className={`venmo-link${pay ? " venmo-pay" : ""}`}
          href={constructVenmoUrl(pay, userTotal.toFixed(2), recipient, note)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <VenmoIcon size={16} /> {pay ? "Pay" : "Request"}
        </a>
      );
    }
  }

  if (compact) {
    return (
      <div className="user-split-compact">
        <span className="user-name">{selectedUser.name}</span>
        <span className="total">${userTotal.toFixed(2)}</span>
        {venmoAction}
      </div>
    );
  }

  return (
    <div className="user-split-total">
      <span className="label user-name">{selectedUser.name}</span>
      <span />

      <span className="label">Subtotal</span>
      <span className="value">${userSubtotal.toFixed(2)}</span>

      <span className="label">Tax ({taxPct.toFixed(1)}%)</span>
      <span className="value">${userTax.toFixed(2)}</span>

      <span className="label">Tip ({tipPct.toFixed(1)}%)</span>
      <span className="value">${userTip.toFixed(2)}</span>

      <div className="total-row">
        <span className="label total">Total</span>
        <div className="venmo-actions">{venmoAction}</div>
      </div>
      <span className="value total">${userTotal.toFixed(2)}</span>
    </div>
  );
};

export default UserSplitTotal;
